import { equalsIgnoreOrder, existsIn, roles } from '../helpers/utils';
import { history } from '../store/configureStore';
import userService from '../services/userServices';
import { updateNotificationCount } from './notifications';
import { osndLocalStorage, storageConstant } from '../helpers/localStorageUtils';
import { ALL_LOCATION_BANNER, LOCAL_STORAGE_KEY, TSM_TOKEN } from '../helpers/const';

// login(user, pass)
//  -> dispatch login request - started login
//  -> first ajax for login
//   --> success - dispatch login success
//   --> failed  - dispatch login failed

export const sessionRequest = () => ({ type: 'SESSION_REQUEST' });
export const sessionResults = (auth) => ({ type: 'SESSION_RESULTS', auth });
export const loginTime = (time) => ({ type: 'LOGIN_TIME', time });

export const loginRequest = (user) => ({ type: 'LOGIN_REQUEST', user });
export const loginSuccess = (roles) => ({ type: 'LOGIN_SUCCESS', roles });
export const loginApiError = (message, form) => ({ type: 'LOGIN_API_ERROR', message, form });

export const loginFormValidation = (form) => ({ type: 'LOGIN_FORM_VALIDATION', form });

export const logoutRequest = () => ({ type: 'LOGOUT_REQUEST' });
export const logoutSuccess = () => ({ type: 'LOGOUT_SUCCESS' });
export const logoutError = () => ({ type: 'LOGOUT_ERROR' });

export const locationRequest = () => ({ type: 'LOCATION_REQUEST' });
export const locationSuccess = (locations, selectedId, selectedName, functionsLimited, enabledFunctions) => ({
    type: 'LOCATION_SUCCESS',
    locations,
    selectedId,
    selectedName,
    functionsLimited,
    enabledFunctions
});
export const locationError = (error) => ({ type: 'LOCATION_ERROR', error });

export const locationSelection = (selectedId) => ({ type: 'LOCATION_SELECTION', selectedId });
export const locationClear = () => ({ type: 'LOCATION_CLEAR' });

export const getLocations = () => (dispatch, getState) => {
    dispatch(locationRequest());

    userService.locations().then(
        (response) => {
            const { data = {} } = response;
            const { functionsLimited, enabledFunctions, assignedLocations: locations } = data;

            const locationsId = Object.keys(locations);
            let selectedId;
            let selectedName;

            if (locations && locationsId.length > 0) {
                [selectedId] = locationsId;
                selectedName = locations[locationsId];
            }

            dispatch(locationSuccess(locations, selectedId, selectedName, functionsLimited, enabledFunctions));
        },
        (error) => {
            const {
                auth: { admin }
            } = getState();
            if (error.status == 412 && admin) {
                dispatch(adminLogout());
            } else {
                dispatch(locationError(error));
            }
        }
    );
};

export const selectLocation = (selectedId) => (dispatch, getState) => {
    const { auth = {} } = getState();

    if (auth.locations[selectedId]) {
        dispatch(locationSelection(selectedId));
    }
};

export const clearLocation = () => (dispatch) => {
    dispatch(locationClear());
};

const updateLinkedEnterprises = (linkedEnterprises, enterpriseId) => {
    const selectedLocations = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES)?.find((el) => el.selected);
    if (!selectedLocations || selectedLocations?.length === 0) {
        osndLocalStorage.setArray(
            storageConstant.LINKED_ENTERPRISES,
            linkedEnterprises?.map((linkedEnterprise) => ({ ...linkedEnterprise, selected: linkedEnterprise.enterpriseId === enterpriseId }))
        );
    }
};

const clearSessionCache = () => {
    // Clear old TSM-TOKEN for MF pages
    sessionStorage.removeItem(TSM_TOKEN);
    sessionStorage.removeItem(ALL_LOCATION_BANNER);
    clearPersistedFilters();
};

const clearPersistedFilters = () => {
    // Clear persisted filters & chart cache
    localStorage.removeItem(LOCAL_STORAGE_KEY);
};

const updateSession = (type, state, dispatch, data) => {
    const {
        signedIn = false,
        email,
        roles: userRoles = [],
        enterpriseId,
        locationId,
        enterpriseName,
        enterpriseEcpdId,
        network,
        createdDate,
        isRentalNib = false,
        hostEnterpriseName,
        hostEnterpriseEcpdId,
        name,
        role,
        networkAddons = [],
        token,
        solutionType,
        linkedEnterprises = [],
        mybizUserId = '',
        isMultiNibSim = false
    } = data;
    const auth = {};
    let changed = false;
    let admin = !!state.admin;

    if (signedIn !== state.signedIn) {
        auth.signedIn = signedIn;
        changed = true;

        // session timeout, redirect to logout
        if (type === 'verify' && signedIn === false) {
            history.push('/logout');
            return;
        }
    }

    auth.enterpriseId = enterpriseId;
    auth.locationId = locationId;
    auth.enterpriseEcpdId = enterpriseEcpdId;
    auth.isRentalNib = isRentalNib;
    auth.hostEnterpriseName = hostEnterpriseName;
    auth.hostEnterpriseEcpdId = hostEnterpriseEcpdId;

    auth.isIBC = solutionType === 'IBC' ? true : false;
    auth.isNDAC = solutionType === 'NDAC' ? true : false;
    auth.isPMEC = networkAddons.includes('PMEC');
    auth.isFLEX = networkAddons.includes('FLEX');
    auth.name = name;
    auth.role = role;
    auth.solutionType = solutionType;
    auth.mybizUserId = mybizUserId;
    auth.isMultiNibSim = isMultiNibSim;

    const enabledLinkedEnterprises = linkedEnterprises.filter(
        (enterprise) => enterprise.permissions?.includes('ROLE_MANAGE_MULTI_NIB_COMBINED_VIEW') || enterprise.enterpriseId === enterpriseId
    );

    if (enabledLinkedEnterprises.length > 1) {
        enabledLinkedEnterprises.push({
            enterpriseName: 'All Locations',
            enterpriseId: 'all_locations'
        });
    }
    auth.linkedEnterprises = enabledLinkedEnterprises;
    updateLinkedEnterprises(enabledLinkedEnterprises, enterpriseId);

    const pos = userRoles.findIndex((x) => x === 'ROLE_MANAGE_NETWORK_5G_NSA_SUPPORT');
    if (data.network === 'LTE' && pos >= 0) {
        userRoles.splice(pos, 1);
    }

    // if(data.network === "NSA_5G"){
    //     const pos = userRoles.findIndex((x) => x === "ROLE_VIEW_INVENTORY");
    //     pos && userRoles.splice(pos, 1);
    // }
    if (createdDate !== state.createdDate) {
        auth.createdDate = createdDate;
        changed = true;
    }

    if (email !== state.email) {
        auth.email = email;
        changed = true;
    }

    // If any changes are made update roles
    if (!equalsIgnoreOrder(state.roles, userRoles)) {
        admin = existsIn(userRoles, roles.ADMIN_USER);
        auth.admin = admin;
        auth.roles = userRoles;
        auth.enterpriseId = enterpriseId;
        auth.network = network;
        auth.networkAddons = networkAddons;
        auth.createdDate = createdDate;
        changed = true;
    }

    if (admin) {
        if (enterpriseId !== state.enterpriseId) {
            if (type === 'verify') {
                auth.enterpriseMismatch = true;
                dispatch(sessionResults(auth));
                return;
            }

            auth.enterpriseId = enterpriseId;
            changed = true;
        }

        if (enterpriseName !== state.enterpriseName) {
            auth.enterpriseName = enterpriseName;
            changed = true;
        }

        if (enterpriseEcpdId !== state.enterpriseEcpdId) {
            auth.enterpriseEcpdId = enterpriseEcpdId;
            changed = true;
        }
        if (createdDate !== state.createdDate) {
            auth.createdDate = createdDate;
            changed = true;
        }

        if (network !== state.network) {
            auth.network = network;
            changed = true;
        }

        if (networkAddons !== state.networkAddons) {
            auth.networkAddons = networkAddons;
            changed = true;
        }
    } else {
        if (enterpriseName !== state.enterpriseName) {
            auth.enterpriseName = enterpriseName;
            changed = true;
        }
    }
    if (changed) {
        dispatch(sessionResults(auth));

        if (type !== 'logout' && signedIn === true && (!admin || (admin && enterpriseId))) {
            token && sessionStorage.setItem(TSM_TOKEN, token);
        } else if (signedIn === false) {
            dispatch(locationClear());
        }
    }
    if (type === 'login' || type === 'logout') {
        clearPersistedFilters();
    }
};

export const verifyAccount =
    (type = 'verify') =>
    (dispatch, getState) => {
        dispatch(sessionRequest());

        userService.session().then(
            (response) => {
                const { auth } = getState();
                updateSession(type, auth, dispatch, response.data);
                if (type === 'init' && response.data.signedIn) {
                    const startTime = new Date();
                    dispatch(loginTime(startTime));
                }
            },
            () => {
                dispatch(sessionResults());
            }
        );
    };

export const login = (user, pass, rememberMe) => (dispatch, getState) =>
    new Promise((resolve, reject) => {
        dispatch(loginRequest(user));

        userService.login(user, pass, rememberMe).then(
            (response) => {
                const { data } = response;
                const { auth } = getState();
                const pos = data.roles.findIndex((x) => x === 'ROLE_MANAGE_NETWORK_5G_NSA_SUPPORT');
                if (data.network === 'LTE' && pos >= 0) {
                    data.roles.splice(pos, 1);
                }
                // if(data.network === "NSA_5G"){
                //     const pos = data.roles.findIndex((x) => x === "ROLE_VIEW_INVENTORY");
                //     pos && data.roles.splice(pos, 1);
                // }
                updateSession('login', auth, dispatch, data);
                dispatch(loginSuccess());
                // loginTime trace on local login page
                const startTime = new Date();
                dispatch(loginTime(startTime));
                resolve(response.data);
            },
            (error) => {
                const { message, form = {} } = error.data;
                dispatch(loginApiError(message, form));
                reject({ message, form });
            }
        );
    });

export const logout = () => (dispatch) => {
    clearSessionCache();
    dispatch(logoutRequest());

    userService.logout().then(
        () => {
            dispatch(logoutSuccess());
            dispatch(locationClear());
        },
        () => {
            dispatch(logoutError());
            history.push('/login');
        }
    );
};

export const adminGetEnterprisesRequest = (filter) => ({ type: 'ADMIN_GET_ENTERPRISES_REQUEST', filter });
export const adminGetEnterprisesSuccess = (status, data) => ({ type: 'ADMIN_GET_ENTERPRISES_SUCCESS', status, data });
export const adminGetEnterprisesApiError = (message, form) => ({ type: 'ADMIN_GET_ENTERPRISES_API_ERROR', message, form });

export const adminGetEnterprises = (json) => (dispatch) =>
    new Promise((resolve, reject) => {
        dispatch(adminGetEnterprisesRequest(json));

        userService.adminGetEnterprises(json).then(
            (response) => {
                const { data } = response;
                dispatch(adminGetEnterprisesSuccess(data));
                resolve(data);
            },
            (error) => {
                const { message, form = {} } = error.data;
                dispatch(adminGetEnterprisesApiError(message, form));
                return reject({ message, form });
            }
        );
    });

export const adminLoginRequest = (enterpriseId) => ({ type: 'ADMIN_LOGIN_REQUEST', enterpriseId });
export const adminLoginSuccess = (status, data) => ({ type: 'ADMIN_LOGIN_SUCCESS', status, data });
export const adminLoginApiError = (message, form) => ({ type: 'ADMIN_LOGIN_API_ERROR', message, form });

export const adminLogin = (json) => (dispatch, getState) =>
    new Promise((resolve, reject) => {
        const { id: enterpriseId, name: enterpriseName, ecpdId: enterpriseEcpdId } = json;

        dispatch(adminLoginRequest(enterpriseId));

        userService.adminLogin(enterpriseId).then(
            (response) => {
                const { data } = response;
                const { auth } = getState();
                const pos = data.roles.findIndex((x) => x === 'ROLE_MANAGE_NETWORK_5G_NSA_SUPPORT');
                if (data.network === 'LTE' && pos >= 0) {
                    data.roles.splice(pos, 1);
                }
                //const results = { ...data, enterpriseName, enterpriseId, enterpriseEcpdId };
                //Modified to verify Rental NIB
                const results = { enterpriseName, enterpriseId, enterpriseEcpdId, ...data };

                updateSession('login', auth, dispatch, results, data);
                resolve(results);
            },
            (error) => {
                const { message, form = {} } = error.data;
                dispatch(adminLoginApiError(message, form));
                return reject({ message, form });
            }
        );
    });

export const adminLogoutRequest = (enterpriseId) => ({ type: 'ADMIN_LOGOUT_REQUEST', enterpriseId });
export const adminLogoutSuccess = (status, data) => ({ type: 'ADMIN_LOGOUT_SUCCESS', status, data });
export const adminLogoutApiError = (message, form) => ({ type: 'ADMIN_LOGOUT_API_ERROR', message, form });

export const adminLogout = () => (dispatch, getState) =>
    new Promise((resolve, reject) => {
        dispatch(adminLogoutRequest());
        const { auth } = getState();

        userService.adminLogout(auth.enterpriseId).then(
            (response) => {
                const { data } = response;

                updateSession('logout', auth, dispatch, data);
                clearSessionCache();
                history.push('/');
                updateNotificationCount(dispatch, 0);
                dispatch(adminLogoutSuccess(data));
                resolve(data);
            },
            (error) => {
                const { message, form = {} } = error.data;
                dispatch(adminLogoutApiError(message, form));
                return reject({ message, form });
            }
        );
    });
